import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { StockBatchModals } from "@/components/stock-batch/stock-batch-modals";
import { StockTransferModals } from "@/components/stock-transfer/stock-transfer-modals";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import useAppStore from "@/store";
import { Plus, X } from "lucide-react";
import { Suspense, useEffect } from "react";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import FullPagination from "@/components/ui/shared/full-pagination";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import type { StockBatch } from "@/store/StockBatch";
import { StockBatchTableColumns } from "@/components/stock-batch/stock-batch-meta";
import type { Product } from "@/store/Product";
import StockBatchStats from "@/components/stock-batch/stock-batch-stats";
import DataPlaceholder from "@/components/ui/shared/data-placeholder";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import TableSettings from "@/components/ui/shared/table-settings";

// 1. Define the shape of search params
type StockBatchSearch = {
  productId?: string;
  page?: number;
  per_page?: number;
};

export const Route = createFileRoute("/(dasboard)/inventory/stock-batchs/")({
  // 2. Validate and provide search params to the component
  validateSearch: (search: Record<string, unknown>): StockBatchSearch => {
    return {
      productId: (search.productId as string) || undefined,
      page: Number(search.page) || 1,
      per_page: Number(search.per_page) || 10,
    };
  },
  component: StockBatchsPage,
});

function StockBatchsPage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const navigate = useNavigate();

  // 3. Get search params from TanStack
  const { productId, page, per_page } = Route.useSearch();

  const tableName = "stock_batchs";
  const tableAttributes = useTableAttributes();
  const product = store.product.current as Product;

  const searchForm = useSimpleForm({
    keyword: "",
    category_id: undefined,
    type_id: undefined,
  });

  useEffect(() => {
    if (store.auth.session) {
      store.warehouse.fetch();
      store.supplier.fetch();

      const initialParams: any = { page, per_page };
      if (productId) {
        initialParams["product_id"] = productId;
        store.product.fetchOne(productId).then((p) => {
          store.product.setCurrent(p);
        });
      }

      store.stockBatch.fetch(initialParams);
    }

    tableAttributes.loadAttributes(tableName, [
      "batch_code",
      "product",
      "unit_price",
      "quantity",
      "qty_sold",
      "quantity_supply_return",
      "quantity_sale_return",
      "quantity_adjusted",
      "quantity_transfer_in",
      "quantity_transfer_out",
      "created_at",
      "updated_at",
      "creator",
      "updater",
      "actions",
    ]);
  }, [store.auth.session, productId]); // Re-run if productId changes in URL

  const handleAutoComplete = (values: any) => {
    store.stockBatch.paginate(
      store.stockBatch.items.filter((item: StockBatch) => {
        const nameMatch = item.batch_code
          ?.toLowerCase()
          .includes(values.keyword.toLowerCase());
        const productNameMatch = item.product.name
          .toLowerCase()
          .includes(values.keyword.toLowerCase());
        return nameMatch || productNameMatch;
      })
    );
  };

  const columns = StockBatchTableColumns({
    store,
    onView: (stock_batch: any) => {
      store.stockBatch.setCurrent(stock_batch);
      store.display.show("view_stock_batch");
    },
    onEdit: (stock_batch: any) => {
      store.stockBatch.setCurrent(stock_batch);
      store.display.show("edit_stock_batch");
    },
    onDelete: async (stock_batch: any) => {
      store.stockBatch.setCurrent(stock_batch);
      store.display.show("delete_stock_batch");
    },
    onReajust: (stock_batch: StockBatch) => {
      store.stockBatch.setCurrent(stock_batch);
      store.display.show("adjust_stock_quantity");
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <Suspense fallback={<DataPlaceholder />}>
      <StockBatchModals />
      <StockTransferModals />

      <div className="mx-auto">
        <header className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold"> {t("Inventory")} </h2>
          <Button
            onClick={() => navigate({ to: "/inventory/stock-batchs/create" })}
          >
            <Plus className="w-4 h-4" />
            {t("Add Inventory")}
          </Button>
        </header>

        <StockBatchStats />

        <div className="mt-4"></div>

        <TableSettings
          tableName={tableName}
          columns={StockBatchTableColumns({ store })}
        >
          <SearchEngine
            autoComplete={handleAutoComplete}
            selects={[]}
            form={searchForm}
            onSubmit={undefined}
          />
        </TableSettings>

        {product && (
          <div className="flex mb-4">
            <div className="bg-white flex border-gray-200 rounded-md gap-4 px-4 py-2">
              <p>{product.name}</p>
              <button
                onClick={() => {
                  store.product.setCurrent(undefined);
                  navigate({
                    from: Route.fullPath,
                    search: (old) => {
                      const { productId, ...rest } = old;
                      return { ...rest, page: 1 };
                    },
                  });
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <SimpleTable
          cellClass="px-2"
          headCellClass="px-2"
          items={store.stockBatch.pagination.data}
          columns={columns}
        />
        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.stockBatch.pagination}
          onGoto={(page: number) =>
            navigate({
              from: Route.fullPath,
              search: (old) => ({ ...old, page }),
            })
          }
          onPerPage={(per_page) =>
            navigate({
              from: Route.fullPath,
              search: (old) => ({ ...old, per_page }),
            })
          }
        />
      </div>
    </Suspense>
  );
}
